import {
  Button,
  Form,
  Input,
  Table,
  Select,
  message,
  TreeSelect,
  Tag,
  Flex,
  Spin,
  Modal,
  Radio,
  Row,
  Col,
} from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import { fetchProduct } from "../../../../../configuration/module/Product/Service/product";
import { fetchCategoryForFilter } from "../../Category/Service/category";
import TextArea from "antd/es/input/TextArea";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchBuilderById,
  postBuilder,
  putBuilder,
} from "../Service/checklistBuilder";

const { Option } = Select;

const ChecklistBuilderForm = ({ mode }) => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [records, setRecords] = useState([]);
  const [product, setProduct] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editData, setEditData] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // Convert product API to TreeSelect format
  const buildTreeData = (nodes) =>
    nodes.map((node) => {
      const isLeaf = !node.children || node.children.length === 0;
      return {
        title: node.productName,
        value: node.id,
        key: node.id,
        selectable: isLeaf,
        disabled: !isLeaf,
        children: node.children ? buildTreeData(node.children) : [],
      };
    });

  // Convert File to Base64
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // remove prefix
      reader.onerror = (error) => reject(error);
    });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const res = await fetchProduct();
        setProduct(buildTreeData(res.data));

        if (editData) {
          // Set product and checklist name
          form.setFieldsValue({
            product: editData.productId,
            checklistName: editData.checklistName,
          });

          // Normalize editData records
          const initialRecords = (editData.records || []).map((r) => ({
            ...r,
            category: r.categoryId,
            key: r.id || Date.now(),
            files: (r.sampleImages || []).map((img, idx) => ({
              uid: `${r.id || "new"}-${idx}`,
              name: `image-${idx + 1}.png`,
              status: "done",
              url: `data:image/png;base64,${img}`,
              originFileObj: null,
            })),
          }));
          setRecords(initialRecords);
        } else {
          form.resetFields();
          setRecords([]);
        }
      } catch (err) {
        message.error("Network error while fetching products");
      } finally {
        setIsLoading(false);
      }
      setIsLoading(true);
      try {
        const res = await fetchCategoryForFilter();
        setCategories(res.data);
      } catch (err) {
        message.error("Network error while fetching categories");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [editData, form]);

  const addRecord = () => {
    setRecords([
      ...records,
      {
        key: Date.now(),
        description: "",
        inputType: "",
        files: [],
        options: [],
      },
    ]);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  const removeRecord = (key) => {
    setRecords(records.filter((r) => r.key !== key));
  };

  // Handle file selection
  const handleFileSelect = (e, record) => {
    const files = Array.from(e.target.files);
    for (let file of files) {
      if (!file.type.startsWith("image/")) {
        message.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        message.error(`${file.name} exceeds 5MB`);
        continue;
      }
      record.files = [
        ...(record.files || []),
        {
          uid: Date.now().toString(),
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          originFileObj: file,
        },
      ];
    }
    setRecords([...records]);
    e.target.value = ""; // reset input
  };

  const columns = [
    {
      title: "Category",
      dataIndex: "category",
      render: (_, record) => (
        <Select
          style={{ width: 150 }}
          value={record.category || record.categoryId}
          disabled={mode === "view"}
          onChange={(val) => {
            record.category = val;
            setRecords([...records]);
          }}
        >
          {categories.map((cat) => (
            <Option key={cat.id} value={cat.id}>
              {cat.category}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record) => (
        <TextArea
          rows={3}
          disabled={mode === "view"}
          value={record.description}
          onChange={(e) => {
            record.description = e.target.value;
            setRecords([...records]);
          }}
        />
      ),
    },
    {
      title: "Input Type",
      dataIndex: "inputType",
      render: (_, record) => (
        <Select
          style={{ width: 150 }}
          value={record.inputType}
          disabled={mode === "view"}
          onChange={(val) => {
            record.inputType = val;
            if (val !== "OPTIONS") {
              record.options = []; // reset if changed away from OPTIONS
            }
            setRecords([...records]);
          }}
        >
          <Option value="OK_NOT_OK">OK / Not OK</Option>
          <Option value="OPTIONS">Options</Option>
          <Option value="STRING">String</Option>
          <Option value="DOUBLE">Number</Option>
        </Select>
      ),
    },
    {
      title: "Options",
      dataIndex: "options",
      render: (_, record) => {
        return mode === "view" ? (
          <Radio.Group
            defaultValue={record.value}
            disabled={mode === "view"}
            onChange={(e) => {
              record.value = e.target.value;
              setRecords([...records]);
            }}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {record.options?.map((opt, idx) => (
              <Radio key={idx} value={opt}>
                {opt}
              </Radio>
            ))}
          </Radio.Group>
        ) : (
          <>
            {record.inputType === "OPTIONS" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {/* Show added options as AntD tags */}
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {(record.options || []).map((opt, idx) => (
                    <Tag
                      key={idx}
                      closable
                      onClose={() => {
                        record.options = record.options.filter(
                          (_, i) => i !== idx
                        );
                        setRecords([...records]);
                      }}
                      color="blue"
                      style={{
                        marginBottom: 4,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0 8px",
                      }}
                    >
                      {opt}
                    </Tag>
                  ))}
                </div>

                {/* Input for adding new option */}
                <Input.Search
                  placeholder="Add option"
                  enterButton="Add"
                  onSearch={(val) => {
                    const newVal = val.trim();
                    if (!newVal) return;
                    const exists = (record.options || []).some(
                      (o) => o.toLowerCase() === newVal.toLowerCase()
                    );
                    if (exists) {
                      message.warning("Duplicate option not allowed");
                      return;
                    }
                    record.options = [...(record.options || []), newVal];
                    setRecords([...records]);
                  }}
                />
              </div>
            )}
          </>
        );
      },
    },
    {
      title: "Sample Images",
      dataIndex: "sampleImages",
      width: 220,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(record.files || []).map((file, idx) => (
              <div key={file.uid} style={{ position: "relative" }}>
                <img
                  src={file.url}
                  alt={file.name}
                  onClick={() => {
                    setModalOpen(true);
                    setSelectedImage(file.url);
                  }}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
                {mode === "view" || (
                  <Button
                    size="small"
                    danger
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      padding: "0 4px",
                      width: "25px",
                      opacity: "0.6",
                    }}
                    onClick={() => {
                      record.files = record.files.filter((_, i) => i !== idx);
                      setRecords([...records]);
                    }}
                  >
                    x
                  </Button>
                )}
              </div>
            ))}
          </div>

          {mode === "view" || (
            <>
              <Button
                type="dashed"
                style={{ width: 120 }}
                onClick={() =>
                  document.getElementById(`file-input-${record.key}`).click()
                }
              >
                Add Image
              </Button>
              <input
                id={`file-input-${record.key}`}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e, record)}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  if (mode !== "view") {
    columns.push({
      title: "Actions",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeRecord(record.key)}
        />
      ),
    });
  }

  const onFinish = async (values) => {
    if (records.length === 0) {
      message.error("Please add at least one record.");
      return;
    }

    for (let i = 0; i < records.length; i++) {
      const r = records[i];
      if (!r.description || !r.inputType) {
        message.error(`Please fill all fields for record ${i + 1}`);
        return;
      }
      if (r.inputType === "OPTIONS" && (!r.options || r.options.length < 2)) {
        message.error(`Record ${i + 1} must have at least two options`);
        return;
      }
      if (r.inputType !== "OPTIONS") {
        r.options = []; // ensure options is empty if not OPTIONS type
      }
    }

    // Convert all files to Base64 before sending
    const recordsWithBase64 = await Promise.all(
      records.map(async (r, index) => {
        const sampleImages = await Promise.all(
          (r.files || []).map(async (f) =>
            f.originFileObj
              ? fileToBase64(f.originFileObj)
              : f.url.split(",")[1]
          )
        );
        return {
          id: r.id,
          categoryId: r.category,
          description: r.description,
          inputType: r.inputType,
          options: r.options || [],
          sequenceNo: index + 1,
          sampleImages,
        };
      })
    );

    const payload = {
      checklistName: values.checklistName,
      productId: values.product,
      listOfCheckListBuilderRecord: recordsWithBase64,
    };

    saveData(payload);
  };

  const saveData = async (data) => {
    setIsLoading(true);
    try {
      if (editData) {
        await putBuilder(data, editData.id);
      } else {
        await postBuilder(data);
      }
      messageApi.open({
        type: "success",
        content: `Checklist template ${
          editData ? "updated" : "added"
        } successfully`,
      });
      navigate({
        pathname: "/check-list-builder/configuration/checklist-builder",
        replace: true,
      });
      form.resetFields();
    } catch (err) {
      messageApi.open({
        type: "error",
        content: "Error while saving execution",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      // Fetch existing data for editing
      const fetchData = async () => {
        const res = await fetchBuilderById(id);
        setEditData(res.data);
      };
      fetchData();
    }
  }, [id]);

  return (
    <Spin spinning={isLoading}>
      <div style={{ fontSize: "1rem", fontWeight: 700, paddingBottom: "10px" }}>
        Checklist Builder
      </div>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="product"
              label="Select Product"
              rules={[{ required: true, message: "Please select a product!" }]}
            >
              <TreeSelect
                treeData={product}
                placeholder="Select Product"
                allowClear
                style={{ width: "100%" }}
                treeDefaultExpandAll={false}
                disabled={mode === "view"}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Checklist Name"
              name="checklistName"
              rules={[
                { required: true, message: "Please enter checklist name!" },
              ]}
            >
              <Input disabled={mode === "view"} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Flex justify="end">
              {mode === "view" || (
                <Form.Item label=" ">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addRecord}
                  >
                    Add Record
                  </Button>
                </Form.Item>
              )}
            </Flex>
          </Col>
        </Row>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={records}
          pagination={false}
          bordered
          scroll={{ x: "max-content" }}
        />

        <Form.Item
          style={{
            marginTop: 20,
            textAlign: "right",
          }}
        >
          <Button
            style={{ marginRight: "1rem" }}
            onClick={() => {
              navigate(
                {
                  pathname:
                    "/check-list-builder/configuration/checklist-builder",
                },
                { replace: true }
              );
            }}
          >
            Cancel
          </Button>
          {mode === "view" || (
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          )}
        </Form.Item>
      </Form>
      <Modal
        title="Image Preview"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={false}
      >
        <img src={selectedImage} alt="Selected" style={{ width: "100%" }} />
      </Modal>
    </Spin>
  );
};

export default ChecklistBuilderForm;

// import { useState, useRef } from "react";

// export default function App() {
//   const [activeInput, setActiveInput] = useState(null);
//   const [log, setLog] = useState([]);
//   const portRef = useRef(null);
//   const readerRef = useRef(null);

//   const handleFocus = (e) => {
//     setActiveInput(e.target);
//   };

//   const connectSerial = async () => {
//     try {
//       portRef.current = await navigator.serial.requestPort();
//       await portRef.current.open({ baudRate: 9600 });
//       console.log("Port opened");

//       const reader = portRef.current.readable.getReader();
//       readerRef.current = reader;

//       while (true) {
//         const { value, done } = await reader.read();
//         if (done) break;
//         if (value) {
//           // Decode bytes
//           const text = new TextDecoder().decode(value);
//           console.log("Raw data:", value, "Decoded:", text);

//           setLog((prev) => [...prev, text]);

//           if (activeInput) {
//             activeInput.value = text;
//             activeInput.dispatchEvent(new Event("input", { bubbles: true }));
//           }
//         }
//       }
//     } catch (err) {
//       console.error("Serial error:", err);
//     }
//   };

//   const disconnectSerial = async () => {
//     try {
//       if (readerRef.current) {
//         await readerRef.current.cancel();
//         readerRef.current.releaseLock();
//       }
//       if (portRef.current) {
//         await portRef.current.close();
//         portRef.current = null;
//         console.log("Serial port closed");
//       }
//     } catch (err) {
//       console.error("Error closing port:", err);
//     }
//   };

//   return (
//     <div style={{ padding: "20px" }}>
//       <h2>Micrometer Input Debug</h2>

//       <button onClick={connectSerial}>🔌 Connect</button>
//       <button onClick={disconnectSerial} style={{ marginLeft: "10px" }}>
//         ❌ Disconnect
//       </button>

//       <div style={{ marginTop: "20px" }}>
//         <input onFocus={handleFocus} placeholder="Field 1" />
//         <br />
//         <input onFocus={handleFocus} placeholder="Field 2" />
//         <br />
//         <input onFocus={handleFocus} placeholder="Field 3" />
//       </div>

//       <h3>Raw Log:</h3>
//       <pre style={{ background: "#eee", padding: "10px", height: "200px", overflow: "auto" }}>
//         {log.join("")}
//       </pre>
//     </div>
//   );
// }
